import { formatDate } from './dateFormat';
import { calculateHorseAge } from './horseAge';

/**
 * Builds the one-line `summary` for the income record created when a sold horse is
 * added to the books from the Horses page. Two shapes, chosen by cost:
 *
 *   Kid (Stallion), age 11 years, $15,000 (Cost $7,000), acquired 09/18/2018, sold 08/03/2024
 *   Guinness (Colt), age 9 months, $4,500 (Cost $0), foaled @ KTMR, sold 02/25/2024
 *
 * Pure function — no React, no API. Every clause is dropped rather than rendered as
 * a dash when its data is missing, so a thin horse record still reads as a sentence.
 */

// income.summary is String(255).
const SUMMARY_MAX_LENGTH = 255;

// Whole dollars. Deliberately not the Horses page's formatCurrency, which shows
// cents — "$15,000.00 (Cost $7,000.00)" reads like a ledger, not a sentence.
const wholeDollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const toAmount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

export const formatSaleAmount = (value) => wholeDollars.format(toAmount(value) ?? 0);

export const buildSaleIncomeSummary = (horse) => {
  if (!horse) return '';

  const name = horse.name?.trim() || horse.registered_name?.trim() || `Horse #${horse.id}`;
  const type = horse.type?.trim();
  const clauses = [type ? `${name} (${type})` : name];

  // Age at the sale, not age today — the horse stops aging on our books when it sells.
  const age = calculateHorseAge(horse.date_of_birth, horse.date_sold || new Date());
  if (horse.date_of_birth && age !== '-') {
    clauses.push(`age ${age}`);
  }

  const cost = toAmount(horse.purchase_amount);
  clauses.push(`${formatSaleAmount(horse.sale_amount)} (Cost ${formatSaleAmount(cost)})`);

  // A ranch-bred horse was never bought, so it has no cost basis. That zero is the
  // only marker we have — there is no bred-here flag on the horse record.
  if (!cost) {
    clauses.push('foaled @ KTMR');
  } else {
    const purchased = formatDate(horse.date_purchased);
    clauses.push(purchased ? `acquired ${purchased}` : 'acquired');
  }

  const sold = formatDate(horse.date_sold);
  if (sold) {
    clauses.push(`sold ${sold}`);
  }

  return clauses.join(', ').slice(0, SUMMARY_MAX_LENGTH);
};

// Mirrors buildSaleIncomeSummary for the "Add to Expense" action on a fully-paid
// purchase, e.g.:
//   Kid (Stallion), age 11 years, $7,000, purchased 09/18/2018
export const buildPurchaseExpenseSummary = (horse) => {
  if (!horse) return '';

  const name = horse.name?.trim() || horse.registered_name?.trim() || `Horse #${horse.id}`;
  const type = horse.type?.trim();
  const clauses = [type ? `${name} (${type})` : name];

  const age = calculateHorseAge(horse.date_of_birth, horse.date_purchased || new Date());
  if (horse.date_of_birth && age !== '-') {
    clauses.push(`age ${age}`);
  }

  clauses.push(formatSaleAmount(horse.purchase_amount));

  const purchased = formatDate(horse.date_purchased);
  clauses.push(purchased ? `purchased ${purchased}` : 'purchased');

  return clauses.join(', ').slice(0, SUMMARY_MAX_LENGTH);
};

export default buildSaleIncomeSummary;
